/**
 * Navigation utilities for React Router
 *
 * Adapted from Vike SSR to work with React Router.
 * These functions replace the original vike/client/router usage.
 */

// Global navigation function set by NavigationSetter component
let globalNavigate: ((path: string, options?: { replace?: boolean }) => void) | null = null;

/**
 * Set the global navigate function (called from App component)
 */
export function setGlobalNavigate(nav: (path: string, options?: { replace?: boolean }) => void): void {
  globalNavigate = nav;
}

/**
 * Navigate to a new path (replaces useNavigate())
 *
 * @example
 * navigate('/home')
 * navigate('/settings?tab=privacy')
 *
 * @param path - The path to navigate to
 * @param replace - If true, replaces current history entry (default: false)
 */
export function navigate(path: string, replace: boolean = false): void {
  if (globalNavigate) {
    globalNavigate(path, { replace });
  } else {
    // Fallback to window.location
    if (replace) {
      window.location.replace(path);
    } else {
      window.location.href = path;
    }
  }
}

/**
 * Get current pathname (replaces useLocation().pathname)
 *
 * @example
 * const pathname = getPathname() // "/settings"
 *
 * @returns Current URL pathname
 */
export function getPathname(): string {
  if (typeof window === 'undefined') return '/';
  return window.location.pathname;
}

/**
 * Get current search string (replaces useLocation().search)
 *
 * @example
 * const search = getSearch() // "?tab=privacy"
 *
 * @returns Current URL search string including "?"
 */
export function getSearch(): string {
  if (typeof window === 'undefined') return '';
  return window.location.search;
}

/**
 * Get current hash (replaces useLocation().hash)
 *
 * @example
 * const hash = getHash() // "#section-1"
 *
 * @returns Current URL hash including "#"
 */
export function getHash(): string {
  if (typeof window === 'undefined') return '';
  return window.location.hash;
}

/**
 * Get full location object (replaces useLocation())
 *
 * @example
 * const location = getLocation()
 * console.log(location.pathname, location.search, location.hash)
 *
 * @returns Object with pathname, search, hash, and href
 */
export function getLocation(): { pathname: string; search: string; hash: string; href: string } {
  if (typeof window === 'undefined') {
    return { pathname: '/', search: '', hash: '', href: '/' };
  }
  return {
    pathname: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash,
    href: window.location.href
  };
}

/**
 * Parse route params from URL pattern (replaces useParams())
 *
 * Note: In React Router, use useParams() hook instead when available.
 * This is a fallback for non-component code.
 *
 * @example
 * // For URL: /reviews/123
 * // With pattern: /reviews/:id
 * const params = parseParams('/reviews/:id')
 * console.log(params.id) // "123"
 *
 * @param pattern - URL pattern with :param syntax
 * @returns Object with param key-value pairs
 */
export function parseParams(pattern: string): Record<string, string> {
  if (typeof window === 'undefined') return {};

  const pathname = window.location.pathname;
  const patternParts = pattern.split('/').filter(Boolean);
  const pathnameParts = pathname.split('/').filter(Boolean);

  const params: Record<string, string> = {};

  patternParts.forEach((part, index) => {
    if (part.startsWith(':')) {
      const paramName = part.slice(1);
      params[paramName] = pathnameParts[index] || '';
    }
  });

  return params;
}

/**
 * Get search params as object (replaces useSearchParams())
 *
 * @example
 * // For URL: /settings?tab=privacy&theme=dark
 * const params = getSearchParams()
 * console.log(params.tab) // "privacy"
 * console.log(params.theme) // "dark"
 *
 * @returns Object with search param key-value pairs
 */
export function getSearchParams(): Record<string, string> {
  if (typeof window === 'undefined') return {};

  const params: Record<string, string> = {};
  const searchParams = new URLSearchParams(window.location.search);

  searchParams.forEach((value, key) => {
    params[key] = value;
  });

  return params;
}

/**
 * Get a single search param value (replaces useSearchParams()[0].get())
 *
 * @example
 * // For URL: /settings?tab=privacy
 * const tab = getSearchParam('tab') // "privacy"
 *
 * @param key - The search param key to get
 * @returns The param value or null if not found
 */
export function getSearchParam(key: string): string | null {
  if (typeof window === 'undefined') return null;
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get(key);
}

/**
 * Set search params programmatically (replaces useSearchParams()[1])
 *
 * @example
 * // Navigate to /settings?tab=privacy&theme=dark
 * setSearchParams({ tab: 'privacy', theme: 'dark' })
 *
 * @param params - Object with param key-value pairs to set
 * @param replace - If true, replaces current history entry (default: false)
 */
export function setSearchParams(params: Record<string, string>, replace: boolean = false): void {
  if (typeof window === 'undefined') return;

  const searchParams = new URLSearchParams(window.location.search);

  // Update params
  Object.entries(params).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') {
      searchParams.delete(key);
    } else {
      searchParams.set(key, value);
    }
  });

  const newSearch = searchParams.toString();
  const newUrl = `${window.location.pathname}${newSearch ? '?' + newSearch : ''}${window.location.hash}`;

  if (replace) {
    window.history.replaceState(null, '', newUrl);
  } else {
    window.history.pushState(null, '', newUrl);
  }
}

/**
 * Navigate back in history (replaces navigate(-1))
 *
 * @example
 * goBack()
 */
export function goBack(): void {
  if (typeof window === 'undefined') return;
  window.history.back();
}

/**
 * Navigate forward in history (replaces navigate(1))
 *
 * @example
 * goForward()
 */
export function goForward(): void {
  if (typeof window === 'undefined') return;
  window.history.forward();
}

/**
 * Check if current path matches a pattern
 *
 * @example
 * if (matchesPath('/settings')) {
 *   console.log('On settings page')
 * }
 *
 * @param pattern - Path pattern to match (supports wildcards)
 * @returns true if current path matches pattern
 */
export function matchesPath(pattern: string): boolean {
  if (typeof window === 'undefined') return false;
  const pathname = window.location.pathname;

  // Exact match
  if (pattern === pathname) return true;

  // Wildcard match (e.g., /settings/*)
  if (pattern.endsWith('/*')) {
    const base = pattern.slice(0, -2);
    return pathname.startsWith(base);
  }

  return false;
}

/**
 * Hook-style useParams for compatibility (replaces react-router's useParams)
 *
 * Extracts params from URL based on a pattern or from the last path segment.
 *
 * @example
 * // For URL: /retrospectives/123
 * const { retroId } = useParams<{ retroId: string }>(); // retroId = "123"
 */
export function useParams<T extends Record<string, string> = Record<string, string>>(): T {
  if (typeof window === 'undefined') return {} as T;

  const pathname = window.location.pathname;
  const parts = pathname.split('/').filter(Boolean);

  // Common patterns - extract last segment as id
  const lastPart = parts[parts.length - 1] || '';
  const secondLastPart = parts[parts.length - 2] || '';

  // Return common param names based on URL pattern
  return {
    id: lastPart,
    retroId: lastPart,
    focusId: lastPart,
    noteId: lastPart,
    cardId: lastPart,
    type: secondLastPart,
  } as unknown as T;
}
